/**
 * Format a duration to a human-readable string.
 *
 * Supports two calling conventions:
 *
 * 1. Plain minutes (backward-compatible):
 *    - < 60 minutes  → "45 min"
 *    - 60 minutes    → "1hr"
 *    - 90 minutes    → "1hr 30min"
 *
 * 2. Typed duration (pass durationType):
 *    - (2, "hours")   → "2hr"
 *    - (1.5, "hours") → "1hr 30min"
 *    - (3, "days")    → "3 days"
 *
 * Case-insensitive durationType matching ("HOURS" == "hours").
 * Returns "" for falsy, zero, NaN, or negative inputs.
 */

export type DurationType = "minutes" | "hours" | "days"

export function formatDuration(
  value: number | string | undefined | null,
  durationType?: string | null,
): string {
  if (!value) return ""

  const raw = typeof value === "string" ? parseFloat(value) : value
  if (Number.isNaN(raw) || raw <= 0) return ""

  // Normalize the typed value to minutes
  let totalMinutes: number
  const type = durationType?.toLowerCase()

  if (type === "hours") {
    totalMinutes = raw * 60
  } else if (type === "days") {
    totalMinutes = raw * 1440
  } else {
    // Default: value is already in minutes
    totalMinutes = raw
  }

  totalMinutes = Math.round(totalMinutes)

  if (totalMinutes <= 0) return ""

  const days = Math.floor(totalMinutes / 1440)
  const hours = Math.floor((totalMinutes % 1440) / 60)
  const remaining = totalMinutes % 60

  // Days path (only reached when durationType is "days" or value >= 1440 minutes)
  if (days > 0) {
    if (hours > 0 && remaining > 0) {
      return `${days} day${days > 1 ? "s" : ""} ${hours}hr ${remaining}min`
    }
    if (hours > 0) {
      return `${days} day${days > 1 ? "s" : ""} ${hours}hr`
    }
    return `${days} day${days > 1 ? "s" : ""}`
  }

  // Hours path
  if (hours === 0) {
    return `${remaining} min`
  }

  if (remaining === 0) {
    return `${hours}hr`
  }

  return `${hours}hr ${remaining}min`
}
